import withStyles from 'isomorphic-style-loader/lib/withStyles';

import React from 'react';
import Panel from 'react-bootstrap/lib/Panel';

import References from '../../../components/Help/Demographics/References';

import s from './AmateursSection.scss';

class AmateursSection extends React.Component {
  render() {
    return (
      <React.Fragment>
        <Panel>
          <Panel.Heading>Amateurs</Panel.Heading>
          <Panel.Body>
            <p>
              The biggest challenge amateur creators, artists and producers face
              is stepping across the line from the amateur realm into the
              professional sphere. That seemingly small gap can feel lightyears
              wide without contacts in the industry or access to solid feedback
              from knowledgeable experts. What amateurs often need more than
              anything else is to connect with people who were in their
              situation at one point in time and understand what it takes to
              make it. As the old saying goes, "It's not what you know, but who
              you know that matters." The plot below, which comes from a 2018
              Jobvite survey, reveals just how much our world operates on
              connections: friendships and personal connections are by far and
              away the main sources job seekers use to discover new job
              opportunities.
              <span className={s.cite}>17</span>
            </p>

            <img
              alt="Data on education"
              className={s.chart}
              src="/assets/images/about/demographics/charts/chart8.jpg"
            />

            <p>
              Despite the digital interconnectivity of our world, it is still
              challenging to form real-life, career-assisting connections.
              Amateur, especially those without formal training, face an uphill
              battle without solid contacts to the industry. With Shadower,
              however, amateurs can receive the kind of knowledge and personal
              connections they need to take their craft to the next level,
              helping them turn their passion into a full-time career.
            </p>

            <p>
              Shadower will be especially useful for amateurs stepping into a
              new, technical field without any prior knowledge. Commonly, the
              more specialized the field, the less beginner tutorials and
              well-written guides are available. If an average user wants to
              learn about a technical field without formal education, he or she
              is more-or-less expected to just grapple with the material for
              years until it becomes comfortable. But for many people, that
              process does not always work. Resources like YouTube, SkillShare,
              Lynda have great content, but often, they lack the specificity
              that can only be acquired by real-world experience.
            </p>
            <References
              referenceList={
                <div>
                  <ol>
                    <li>
                      <a
                        href="http://youthtruthsurvey.org/wp-content/uploads/2016/01/YouthTruth-Learning-From-Student-Voice-College-and-Career-Readiness-2016.pdf"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        YouthTruth Student Survey (2016)
                      </a>
                      ,&nbsp;
                      <i>
                        Most High Schoolers Feel Unprepared for College and
                        Careers.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.fastweb.com/career-planning/articles/how-to-arrange-and-conduct-a-job-shadow"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        FastWeb (2018)
                      </a>
                      ,&nbsp;
                      <i>How to Manage and Conduct a Job Shadow.</i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/524033/parents-children-tutoring-pay/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        HSBC (2018)
                      </a>
                      ,&nbsp;
                      <i>
                        Share of parents who have paid or would pay for tutoring
                        for their child in selected countries in 2015.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://nces.ed.gov/pubs2018/2018434.pdf"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        NCES (2017)
                      </a>
                      ,&nbsp;
                      <i>
                        College students tend to change majors when they find
                        the one they really love.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/731339/factors-that-would-have-helped-students-to-feel-more-prepared-for-their-careers-us/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Hanover Research, McGraw-Hill (2017)
                      </a>
                      ,&nbsp;
                      <i>
                        Share of factors that would have made students feel more
                        prepared for their careers in the United States in 2017.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.chronicle.com/article/What-s-Wrong-With-Required/242727"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        The Chronicle of Higher Education (2018)
                      </a>
                      ,&nbsp;
                      <i>What's Wrong with Required Internships?</i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/673316/top-fields-for-internship-positions/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        New York Times, Burning Glass (2016)
                      </a>
                      ,&nbsp;
                      <i>
                        Occupational fields with the largest number of
                        internship postings in the United States from September
                        2015 to October 2016.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.burning-glass.com/research-project/internships-2015/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Burning Glass (2015)
                      </a>
                      ,&nbsp;
                      <i>
                        No Experience Necessary? The State of American
                        Internships 2015.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/782547/number-of-schools-offering-occupational-education-us-by-state/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        NCES (2013)
                      </a>
                      ,&nbsp;
                      <i>
                        Number of postsecondary schools offering occupation
                        education in the U.S. in 2013, by state.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://highered.colorado.gov/Data/InstSelect.aspx"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        CHDE (2017)
                      </a>
                      ,&nbsp;
                      <i>Search Colleges and Universities.</i>
                    </li>

                    <li>
                      <a
                        href="https://nces.ed.gov/programs/digest/d17/tables/dt17_105.50.asp?current=yes"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        NCES (2016)
                      </a>
                      ,&nbsp;
                      <i>
                        Number of educational institutions, by level and control
                        of institution: Selected years, 1980-81 through 2015-16.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/782528/number-of-schools-offering-occupational-education-us-by-school-type/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        NCES (2014)
                      </a>
                      ,&nbsp;
                      <i>
                        Number of postsecondary schools offering occupational
                        education in the U.S. from 2000 to 2014, by school type.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/334116/reasons-for-us-online-non-degree-classes-by-age/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Online Computer Library Center (2014)
                      </a>
                      ,&nbsp;
                      <i>
                        U.S. online learner reasons for taking online non-degree
                        classes as of January 2014, by age.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/334033/us-online-classes-tutorial-usage-income-levels/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Online Computer Library Center (2014)
                      </a>
                      ,&nbsp;
                      <i>
                        Share of U.S. online population who have taken online
                        classes or tutorials as of January 2014, by income
                        levels.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/334040/us-online-classes-tutorial-usage-education-levels/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Online Computer Library Center (2014)
                      </a>
                      ,&nbsp;
                      <i>
                        Share of U.S. online population who have taken online
                        classes or tutorials as of January 2014, by education
                        levels.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/184236/us-online-classes-tutrial-usage-age/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Online Computer Library Center (2014)
                      </a>
                      ,&nbsp;
                      <i>
                        Share of U.S. online population who have taken online
                        classes or tutorials as of January 2014, by age.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.statista.com/statistics/862627/us-employment-market-sources-of-new-opportunities-for-jobseekers/"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Jobvite (2018)
                      </a>
                      ,&nbsp;
                      <i>
                        Main sources to learn of new opportunities for U.S. job
                        seekers in 2018.
                      </i>
                    </li>

                    <li>
                      <a
                        href="https://www.payscale.com/research/US/Job=Small_Business_Owner/Salary"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        REF (2017)
                      </a>
                      ,&nbsp;
                      <i>Small Business Owner Salary.</i>
                    </li>
                  </ol>
                </div>
              }
              referenceTitle="References"
            />
          </Panel.Body>
        </Panel>
      </React.Fragment>
    );
  }
}

export default withStyles(s)(AmateursSection);
